// Materials-tab body for the Inventory panel. Extracted from the
// orchestrator so `ManufacturingInventoryPanel.tsx` stays under the
// file-size hygiene cap after picking up the duplicate-finder + bulk-
// category-reassign affordances.
//
// The orchestrator still owns all data + callbacks; this component is
// purely presentational composition of:
//   - Actions row (+ duplicates toggle button)
//   - Optional duplicate-finder panel
//   - Optional bulk-category-reassign toolbar
//   - Optional bulk-delete / restore toolbar (dev-sync 184bc79f)
//   - Optional bulk material replace toolbar (MUS-1)
//   - Material-use onboarding tip + “Make interchangeable…” (MUS UX)
//   - The paginated materials list (with optional checkbox column +
//     per-row Delete / Restore button driven by the new flow hook)
import { useMemo, useState } from "react";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingBulkCategoryToolbar } from "./ManufacturingBulkCategoryToolbar";
import { ManufacturingBulkDeleteToolbar } from "./ManufacturingBulkDeleteToolbar";
import { ManufacturingBulkMaterialReplaceToolbar } from "./ManufacturingBulkMaterialReplaceToolbar";
import { ManufacturingInventoryAllMaterials } from "./ManufacturingInventoryAllMaterials";
import { ManufacturingMaterialDuplicatesPanel } from "./ManufacturingMaterialDuplicatesPanel";
import { ManufacturingMaterialUseExplainer } from "./ManufacturingMaterialUseExplainer";
import { ManufacturingMaterialUseFromMaterialsModal } from "./ManufacturingMaterialUseFromMaterialsModal";
import { ManufacturingMaterialsActions } from "./ManufacturingMaterialsActions";

const TIP_DISMISS_KEY = "mfg.materialUse.inventoryTip.dismissed";

type DeactivateResult = { ok: true } | { ok: false; error: string };

type Props = {
  allMaterials: ReadonlyArray<MfgMaterial>;
  filteredMaterials: MfgMaterial[];
  existingCategories: ReadonlyArray<string>;
  gateEnabled: boolean;
  busy: boolean;
  duplicatesOpen: boolean;
  onToggleDuplicates: () => void;
  onCloseDuplicates: () => void;
  onCreate: () => void;
  onEdit: (material: MfgMaterial) => void;
  onOpenTransactions?: (material: MfgMaterial) => void;
  onOpenSds?: (material: MfgMaterial) => void;
  onBulkImport: () => void;
  onTemplate: () => void;
  onInventoryCount: () => void;
  onSdsBook: () => void;
  onDeactivateMaterial: (material: MfgMaterial) => Promise<DeactivateResult>;
  selectedMaterialIds: ReadonlySet<string>;
  onToggleSelection: (id: string) => void;
  onToggleSelectAllOnPage: (ids: string[], selectAll: boolean) => void;
  onClearSelection: () => void;
  onBulkApplied: (summary: { updated: number; failed: number }) => void;
  onMaterialReplaceApplied: (summary: {
    updated: number;
    recipes: number;
    source_deactivated: boolean;
  }) => void;
  onMaterialUseCreated: (summary: { name: string; members: number }) => void;
  onRequestDeleteMaterial: (material: MfgMaterial) => void;
  onRequestRestoreMaterial: (material: MfgMaterial) => void;
  onRequestPermanentDeleteMaterial?: (material: MfgMaterial) => void;
  onRequestBulkDelete: (materialIds: ReadonlyArray<string>) => void;
  onRequestBulkRestore: (materialIds: ReadonlyArray<string>) => void;
  /** Org admin/owner — enables Keep + Merge in the duplicate finder. */
  canMergeDuplicates?: boolean;
  onMaterialMerged?: (summary: { fromName: string; toName: string }) => void;
};

export function ManufacturingInventoryMaterialsTabBody(props: Props) {
  const [tipDismissed, setTipDismissed] = useState(() => {
    try {
      return localStorage.getItem(TIP_DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [useModalOpen, setUseModalOpen] = useState(false);

  const selectionTier = useMemo(() => {
    if (props.selectedMaterialIds.size === 0) {
      return { allActive: false, allInactive: false };
    }
    const byId = new Map(props.allMaterials.map((m) => [m.id, m]));
    let allActive = true;
    let allInactive = true;
    for (const id of props.selectedMaterialIds) {
      const m = byId.get(id);
      if (!m) continue;
      if (m.is_active) allInactive = false;
      else allActive = false;
    }
    return { allActive, allInactive };
  }, [props.selectedMaterialIds, props.allMaterials]);

  const selectedIdsArray = useMemo(
    () => Array.from(props.selectedMaterialIds),
    [props.selectedMaterialIds]
  );

  const selectedMaterials = useMemo(() => {
    const byId = new Map(props.allMaterials.map((m) => [m.id, m]));
    return selectedIdsArray
      .map((id) => byId.get(id))
      .filter((m): m is MfgMaterial => Boolean(m));
  }, [props.allMaterials, selectedIdsArray]);

  function dismissTip() {
    try {
      localStorage.setItem(TIP_DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setTipDismissed(true);
  }

  return (
    <div className="space-y-3">
      <ManufacturingMaterialsActions
        filtered={props.filteredMaterials}
        gateEnabled={props.gateEnabled}
        busy={props.busy}
        onCreate={props.onCreate}
        onBulkImport={props.onBulkImport}
        onTemplate={props.onTemplate}
        onInventoryCount={props.onInventoryCount}
        onSdsBook={props.onSdsBook}
        onToggleDuplicates={props.onToggleDuplicates}
        duplicatesOpen={props.duplicatesOpen}
      />
      {!tipDismissed ? (
        <ManufacturingMaterialUseExplainer
          variant="inventory"
          onDismiss={dismissTip}
          action={
            props.selectedMaterialIds.size >= 2 ? (
              <button
                type="button"
                className={ghostBtnClass}
                disabled={props.busy || !props.gateEnabled}
                onClick={() => setUseModalOpen(true)}
              >
                Make selected interchangeable…
              </button>
            ) : (
              <p className="text-xs text-sky-900/80 dark:text-sky-100/75">
                Tip: select two or more materials below to start from Inventory.
              </p>
            )
          }
        />
      ) : null}
      {props.duplicatesOpen ? (
        <ManufacturingMaterialDuplicatesPanel
          materials={props.allMaterials}
          busy={props.busy}
          canMerge={!!props.canMergeDuplicates}
          onClose={props.onCloseDuplicates}
          onEdit={props.onEdit}
          onDeactivate={props.onDeactivateMaterial}
          onMerged={(summary) => props.onMaterialMerged?.(summary)}
        />
      ) : null}
      {props.selectedMaterialIds.size > 0 ? (
        <>
          <ManufacturingBulkDeleteToolbar
            selectedIds={selectedIdsArray}
            allSelectedActive={selectionTier.allActive}
            allSelectedInactive={selectionTier.allInactive}
            busy={props.busy}
            onClear={props.onClearSelection}
            onRequestBulkDelete={() => props.onRequestBulkDelete(selectedIdsArray)}
            onRequestBulkRestore={() => props.onRequestBulkRestore(selectedIdsArray)}
          />
          {props.selectedMaterialIds.size >= 2 ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-sky-200 bg-sky-50/70 px-2 py-1.5 dark:border-sky-900/50 dark:bg-sky-950/25">
              <span className="text-xs text-sky-950 dark:text-sky-100">
                {props.selectedMaterialIds.size} materials selected
              </span>
              <button
                type="button"
                className={
                  "rounded-md bg-sky-700 px-2.5 py-1 text-xs font-semibold text-white hover:bg-sky-800 " +
                  "disabled:cursor-not-allowed disabled:opacity-60"
                }
                disabled={props.busy || !props.gateEnabled}
                onClick={() => setUseModalOpen(true)}
              >
                Make interchangeable…
              </button>
            </div>
          ) : null}
          <ManufacturingBulkMaterialReplaceToolbar
            selectedIds={selectedIdsArray}
            allMaterials={props.allMaterials}
            busy={props.busy}
            onClear={props.onClearSelection}
            onApplied={props.onMaterialReplaceApplied}
          />
          <ManufacturingBulkCategoryToolbar
            selectedIds={selectedIdsArray}
            existingCategories={props.existingCategories}
            busy={props.busy}
            onClear={props.onClearSelection}
            onApplied={props.onBulkApplied}
          />
        </>
      ) : null}
      <ManufacturingInventoryAllMaterials
        materials={props.filteredMaterials}
        busy={props.busy}
        onEdit={props.onEdit}
        onOpenTransactions={props.onOpenTransactions}
        onOpenSds={props.onOpenSds}
        onRequestDelete={props.onRequestDeleteMaterial}
        onRequestRestore={props.onRequestRestoreMaterial}
        onRequestPermanentDelete={props.onRequestPermanentDeleteMaterial}
        selectedIds={props.selectedMaterialIds}
        onToggleSelection={props.onToggleSelection}
        onToggleSelectAllOnPage={props.onToggleSelectAllOnPage}
      />
      <ManufacturingMaterialUseFromMaterialsModal
        open={useModalOpen}
        materials={selectedMaterials}
        onClose={() => setUseModalOpen(false)}
        onCreated={(use, members) => {
          props.onClearSelection();
          props.onMaterialUseCreated({ name: use.name, members });
        }}
      />
    </div>
  );
}
